import { Fragment } from 'react';

import { Check } from '../../icons/lucide/Check';
import { Platform } from '../../platform';
import { Text } from '../../primitives/Text';
import { View } from '../../primitives/View';
import { useTheme } from '../../theme';
import { cn } from '../../utils/cn';
import { subTestID } from '../../utils/testId';
import { resolveStepperIconColor, STEPPER_ICON_SIZE_FOR } from './Stepper.icons';
import type {
  StepperProps,
  StepperSize,
  StepperStep,
  StepperStepState,
  StepperType,
} from './Stepper.types';
import {
  STEPPER_CONNECTOR_CLASS,
  STEPPER_DESCRIPTION_CLASS,
  STEPPER_LABEL_CLASS,
  stepperCircleVariants,
  stepperDotVariants,
  stepperNumberVariants,
  stepperStepRowVariants,
} from './Stepper.variants';

const IS_WEB = Platform.OS === 'web';

/** Width of the vertical-connector column = the circle diameter per size. */
const CONNECTOR_COLUMN_FOR: Record<StepperSize, string> = {
  small: 'w-6',
  medium: 'w-8',
  large: 'w-11',
};

/**
 * Cross-platform Stepper — a multi-step progress indicator with numbered,
 * icon, or dot-indicator circles, optional labels/descriptions, and connector
 * lines. Renders identically on web and React Native. Figma: Stepper frames
 * on the "Progress indicators" page (`2812:50376` · `2812:50180` ·
 * `2812:50467`).
 *
 * @example
 * ```tsx
 * import { Stepper } from '@flashka/shared/ui';
 *
 * <Stepper
 *   currentStep={1}
 *   steps={[
 *     { label: 'Step 1', description: 'Pick a deck' },
 *     { label: 'Step 2', description: 'Generate cards' },
 *     { label: 'Step 3', description: 'Start studying' },
 *   ]}
 * />
 *
 * // Vertical, dot indicators, no labels
 * <Stepper
 *   type="indicator"
 *   orientation="vertical"
 *   currentStep={0}
 *   steps={[{}, {}, {}]}
 * />
 * ```
 */
export function Stepper({
  steps,
  currentStep,
  type = 'numbered',
  size = 'small',
  orientation = 'horizontal',
  className,
  accessibilityLabel,
  testID,
  webProps,
  nativeProps,
}: StepperProps) {
  const horizontal = orientation === 'horizontal';

  return (
    <View
      className={cn(
        horizontal ? 'w-full flex-row items-center gap-2' : 'items-start',
        className,
      )}
      accessibilityLabel={accessibilityLabel}
      testID={testID}
      webProps={webProps}
      nativeProps={nativeProps}>
      {steps.map((step, index) => {
        const state = step.state ?? deriveState(index, currentStep);
        return (
          <Fragment key={index}>
            {index > 0 ? (
              horizontal ? (
                <View className={cn('h-px min-w-6 flex-1', STEPPER_CONNECTOR_CLASS)} />
              ) : (
                <View className={cn('items-center py-1', CONNECTOR_COLUMN_FOR[size])}>
                  <View className={cn('min-h-8 w-px', STEPPER_CONNECTOR_CLASS)} />
                </View>
              )
            ) : null}
            <StepItem
              step={step}
              index={index}
              state={state}
              type={type}
              size={size}
              horizontal={horizontal}
              testID={subTestID(testID, `step-${index}`)}
            />
          </Fragment>
        );
      })}
    </View>
  );
}
(Stepper as { displayName?: string }).displayName = 'Stepper';

function deriveState(index: number, currentStep: number | undefined): StepperStepState {
  if (currentStep == null) return 'idle';
  if (index < currentStep) return 'completed';
  if (index === currentStep) return 'active';
  return 'idle';
}

function StepItem({
  step,
  index,
  state,
  type,
  size,
  horizontal,
  testID,
}: {
  step: StepperStep;
  index: number;
  state: StepperStepState;
  type: StepperType;
  size: StepperSize;
  horizontal: boolean;
  testID?: string;
}) {
  const hasText = Boolean(step.label || step.description);
  // Horizontal labels stay on one line. The active step keeps its name, up to
  // 40% of the row; every other label is cut to 96px even when there is room
  // (full name on hover), and shrinks further when the row runs short — ten
  // long document names in the page-selection modal otherwise wrapped into
  // an unreadable block.
  const active = state === 'active';
  const fit = horizontal ? (active ? 'max-w-[40%]' : 'shrink') : '';
  return (
    <View
      className={cn(
        stepperStepRowVariants({ size, disabled: state === 'disabled' }),
        fit,
      )}
      testID={testID}
      accessibilityLabel={step.label}>
      <View className={stepperCircleVariants({ size, state, type })}>
        <StepContent step={step} index={index} state={state} type={type} size={size} />
      </View>
      {hasText ? (
        // Clipped when horizontal: squeezed below one character, a label is
        // dropped rather than spilling over the connector.
        <View className={cn('shrink items-start', horizontal && 'overflow-hidden')}>
          {step.label ? (
            <Text
              // break-all: a filename is one long word, and a clamp only
              // ellipsizes where it can break the line.
              className={cn(
                STEPPER_LABEL_CLASS,
                horizontal && 'break-all',
                horizontal && !active && 'max-w-24',
              )}
              numberOfLines={horizontal ? 1 : undefined}
              webProps={horizontal ? { title: step.label } : undefined}>
              {step.label}
            </Text>
          ) : null}
          {step.description ? (
            <Text className={STEPPER_DESCRIPTION_CLASS}>{step.description}</Text>
          ) : null}
        </View>
      ) : null}
    </View>
  );
}

function StepContent({
  step,
  index,
  state,
  type,
  size,
}: {
  step: StepperStep;
  index: number;
  state: StepperStepState;
  type: StepperType;
  size: StepperSize;
}) {
  const { isDark } = useTheme();

  if (type === 'indicator') {
    return <View className={stepperDotVariants({ size, state })} />;
  }

  // Completed steps always show the check — including `numbered` (per the
  // Figma "Steps+label / success" symbols, where the number swaps for it).
  const showIcon = type === 'icon' || state === 'completed';
  if (showIcon) {
    const Icon = (type === 'icon' && step.icon) || Check;
    // Web icons inherit the circle's text-* via `currentColor`; native needs
    // the resolved hex (react-native-svg ignores CSS colour inheritance).
    const color = IS_WEB ? 'currentColor' : resolveStepperIconColor(state, isDark);
    return <Icon color={color} size={STEPPER_ICON_SIZE_FOR[size]} />;
  }

  return <Text className={stepperNumberVariants({ size, state })}>{index + 1}</Text>;
}
